/**
 * @fileoverview `ModuleDegradeNote` (WEB-719) — the honest empty-module body.
 *
 * Renders in place of `ModuleRowList` whenever `ResolvedEventModule.hasContent`
 * is false. A dashed-border, sunken-fill block with a small leading dot and
 * one paragraph of lexicon copy. It is a statement of fact, not a warning:
 * no amber, no error icon, never louder than a populated module.
 *
 * Copy is caller-supplied (the section chrome looks it up from
 * `EVENT_MODULE_DEGRADE_COPY`). This file never reads a slug or a lexicon.
 */

import type { ReactElement } from 'react';

import { cn } from '@/components/atoms/primitives/utils';

export interface ModuleDegradeNoteProps {
  /** One paragraph from `getEventModuleDegradeCopy`. */
  copy: string;
  /** Optional additional class names on the note. */
  className?: string;
}

/**
 * `ModuleDegradeNote` — quiet, factual, server-safe. The leading dot is
 * decorative (`aria-hidden`); the paragraph is the accessible content.
 */
export function ModuleDegradeNote({ copy, className }: ModuleDegradeNoteProps): ReactElement {
  return (
    <div
      data-testid="module-degrade-note"
      className={cn(
        'flex items-start gap-3 rounded-ds-md border border-dashed border-ds-border bg-bg-sunken px-4 py-3.5',
        className,
      )}
    >
      <span
        aria-hidden="true"
        data-testid="module-degrade-note-dot"
        className="mt-1.5 size-1.5 shrink-0 rounded-full bg-fg-3"
      />
      <p
        data-testid="module-degrade-note-copy"
        className="m-0 min-w-0 text-(length:--evt-body) text-fg-2"
      >
        {copy}
      </p>
    </div>
  );
}
